import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { getErrorMessage } from "@/lib/errors";

export default function StockBalances() {
  const [balances, setBalances] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const loadAlerts = () => api.get("/inventory/alerts").then(({ data }) => setAlerts(data)).catch((err) => toast.error(getErrorMessage(err)));

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get("/inventory/stock-balances"), api.get("/inventory/items"), api.get("/inventory/warehouses"), api.get("/inventory/alerts")])
      .then(([balRes, itemRes, whRes, alertRes]) => { setBalances(balRes.data); setItems(itemRes.data); setWarehouses(whRes.data); setAlerts(alertRes.data); })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  const itemLabel = (id) => { const i = items.find((it) => it.id === id); return i ? `${i.code} — ${i.name}` : id; };
  const warehouseName = (id) => warehouses.find((w) => w.id === id)?.name || id;

  const scanNow = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/inventory/alerts/scan");
      toast.success(`Scan complete: ${data.open_total} open, ${data.created} new, ${data.resolved} resolved`);
      loadAlerts();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setScanning(false);
    }
  };

  const acknowledge = async (alert) => {
    try {
      await api.patch(`/inventory/alerts/${alert.id}`, { status: "RESOLVED" });
      loadAlerts();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="stock-balances-page">
      <PageHeader title="Stock & Balances" description="Current on-hand quantity and weighted-average cost per item, per warehouse." action={<Button size="sm" variant="outline" disabled={scanning} onClick={scanNow} data-testid="scan-low-stock-button">{scanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Scan for low stock</Button>} />

      {alerts.length > 0 && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/5" data-testid="low-stock-alerts-panel">
          <div className="flex items-center gap-2 border-b border-rose-500/20 px-4 py-3">
            <AlertTriangle className="h-4 w-4 text-rose-400" />
            <h2 className="text-sm font-semibold text-foreground">Low stock alerts ({alerts.length})</h2>
          </div>
          <div className="divide-y divide-border">
            {alerts.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`low-stock-alert-row-${a.id}`}>
                <div>
                  <p className="text-sm text-foreground">{a.title}</p>
                  <p className="text-xs text-muted-foreground">{a.detail} {a.project_id ? "· Tied to project" : "· Org-wide"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={a.severity === "CRITICAL" || a.severity === "HIGH" ? "destructive" : "secondary"}>{a.severity}</Badge>
                  <Button size="sm" variant="ghost" onClick={() => acknowledge(a)} data-testid={`resolve-alert-button-${a.id}`}><Check className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border bg-card">
        {!loading && balances.length === 0 ? (
          <EmptyState title="No stock movements yet" description="Receive against a GRN or a manual receipt to see balances here." testId="stock-balances-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Warehouse</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Avg cost</TableHead><TableHead className="text-right">Value</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {balances.map((b) => (
                <TableRow key={b.id} data-testid={`stock-balance-row-${b.item_id}-${b.warehouse_id}`}>
                  <TableCell className="text-foreground">{itemLabel(b.item_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{warehouseName(b.warehouse_id)}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{b.qty}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">{b.avg_cost}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">{b.value}</TableCell>
                  <TableCell>
                    {b.low_stock && <Badge variant="destructive" data-testid={`low-stock-badge-${b.item_id}`}><AlertTriangle className="mr-1 h-3 w-3" /> Low stock</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
